import { cn } from "@bcc/lib/utils";
import type { AdminAttributeField } from "@shared/types/admin-product-update";
import { Pencil } from "lucide-react";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AdminAttributePopover } from "./admin-attribute-popover";
import type { AdminCatalogueRow } from "./admin-catalogue-rows";
import { type AdminEditLock, isEditableRow, useAdminProductEdit } from "./admin-product-edit-context";
import { ATTRIBUTE_LABEL_KEYS, setAdminAttributeEditTestId, setAdminWebStatusEditTestId } from "./admin-product-edit.utils";
import { AdminWebStatusPopover } from "./admin-web-status-popover";

type EditTriggerProps = ComponentPropsWithoutRef<"button"> & { readonly label: string; readonly testId: string };

// forwardRef so a Radix PopoverTrigger can anchor to it.
const EditTrigger = forwardRef<HTMLButtonElement, EditTriggerProps>(({ label, testId, className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={label}
    title={label}
    className={cn(
      "inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent",
      className
    )}
    {...props}
    // After the spread: a Radix PopoverTrigger passes its own default test id down to the child it wraps.
    data-testid={testId}
  >
    <Pencil className="h-3 w-3" />
  </button>
));
EditTrigger.displayName = "EditTrigger";

const CellLayout = ({ children, trigger }: { readonly children: ReactNode; readonly trigger: ReactNode }) => (
  <div className="flex items-center gap-1">
    {children}
    {trigger}
  </div>
);

/** Why an edit is unavailable, as the disabled button's label. */
const LOCK_LABEL_KEYS = {
  incomplete: "admin.productCatalogue.edit.locks.incomplete",
  noStorefrontStatus: "admin.productCatalogue.edit.locks.noStorefrontStatus",
  submitting: "admin.productCatalogue.edit.locks.submitting",
  submitted: "admin.productCatalogue.edit.submitted",
} as const satisfies Record<AdminEditLock, string>;

interface EditableCellProps {
  readonly lock: AdminEditLock | undefined;
  readonly label: string;
  readonly testId: string;
  /** Wraps the trigger in its popover; not called for an incomplete row, which has nothing to edit. */
  readonly renderEditor: (trigger: ReactNode) => ReactNode;
  readonly children: ReactNode;
}

// The popover stays mounted while a request is in flight, so an open editor isn't torn down mid-submit; its trigger
// is disabled instead, which also stops it being reopened for a duplicate.
function EditableCell({ lock, label, testId, renderEditor, children }: EditableCellProps) {
  const { t } = useTranslation();
  const trigger = <EditTrigger disabled={lock !== undefined} label={lock ? t(LOCK_LABEL_KEYS[lock]) : label} testId={testId} />;
  return (
    <CellLayout trigger={lock === "incomplete" || lock === "noStorefrontStatus" ? trigger : renderEditor(trigger)}>{children}</CellLayout>
  );
}

interface AdminAttributeCellProps {
  readonly field: AdminAttributeField;
  readonly row: AdminCatalogueRow;
  readonly children: ReactNode;
}

/**
 * The read-only cell plus an edit button. Outside the editable page, or on a product placeholder row without a SKU,
 * it is just the cell; when the edit isn't available the button is disabled and says why.
 */
export function AdminAttributeCell({ field, row, children }: AdminAttributeCellProps) {
  const { t } = useTranslation();
  const edit = useAdminProductEdit();
  if (!edit || !row.itemNumber) return <>{children}</>;
  const lock = edit.getAttributeLock(row, field);
  return (
    <EditableCell
      lock={lock}
      label={t("admin.productCatalogue.edit.title", { field: t(ATTRIBUTE_LABEL_KEYS[field]) })}
      testId={setAdminAttributeEditTestId(`${field}-edit-${row.id}`)}
      renderEditor={(trigger) =>
        isEditableRow(row) ? (
          <AdminAttributePopover field={field} row={row}>
            {trigger}
          </AdminAttributePopover>
        ) : (
          trigger
        )
      }
    >
      {children}
    </EditableCell>
  );
}

/** Web status edit: needs the SKU's own status on the region's storefront channel, which is what the change targets. */
export function AdminWebStatusCell({ row, children }: { readonly row: AdminCatalogueRow; readonly children: ReactNode }) {
  const { t } = useTranslation();
  const edit = useAdminProductEdit();
  if (!edit || !row.itemNumber || !row.webStatus) return <>{children}</>;
  return (
    <EditableCell
      lock={edit.getStatusLock(row)}
      label={t("admin.productCatalogue.edit.statusTitle")}
      testId={setAdminWebStatusEditTestId(`edit-${row.id}`)}
      renderEditor={(trigger) => (isEditableRow(row) ? <AdminWebStatusPopover row={row}>{trigger}</AdminWebStatusPopover> : trigger)}
    >
      {children}
    </EditableCell>
  );
}
